import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { WizardShell } from '@/components/parenting-plan';
import { Input } from '@/components/Input';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import { DIVORCE_JURISDICTIONS } from '@/lib/divorce';
import {
  DOC_PREP_DISCLAIMER,
  DV_SCREEN_HINT,
  DV_SCREEN_QUESTION,
} from '@/lib/divorce-legal';

/* ------------------------------------------------------------------ types */

type Party = {
  name?: string;
  former_name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
};

type Child = {
  name?: string;
  dob?: string;
  lives_with?: 'petitioner' | 'respondent' | 'both' | 'other';
};

export type DivorceAnswers = {
  filing_mode?: 'sole' | 'joint';
  petitioner?: Party;
  respondent?: Party;
  marriage?: {
    date?: string;
    city?: string;
    state?: string;
    separation_date?: string;
    common_law?: boolean;
  };
  residency?: {
    months_in_state?: number;
    months_in_county?: number;
    county?: string;
    military?: boolean;
  };
  children?: Child[];
  property?: {
    has_real_estate?: boolean;
    has_retirement?: boolean;
    has_business?: boolean;
  };
  debts?: { description?: string; responsible?: string }[];
  support?: {
    child_support_requested?: boolean;
    spousal_support_requested?: boolean;
  };
  agreement?: {
    schedule_slug?: string;
    parenting_plan_claim_token?: string;
    holidays?: string;
    decision_making?: 'joint' | 'petitioner' | 'respondent';
    expense_split?: string;
  };
  service?: {
    spouse_will_sign_waiver?: boolean;
    spouse_address_known?: boolean;
    same_household?: boolean;
  };
  fee_waiver?: {
    requested?: boolean;
    household_size?: number;
    monthly_income_cents?: number;
  };
  eligibility?: Record<string, boolean | undefined>;
  name_change_requested?: boolean;
};

type GateReason = { code: string; message: string; resource_url?: string | null };

type EligibilityResult = {
  status: 'eligible' | 'blocked' | 'wait';
  blocked_category?: string | null;
  reasons: GateReason[];
  warnings: GateReason[];
};

type DraftResponse = {
  claim_token: string;
  jurisdiction_code: string | null;
  answers: DivorceAnswers;
  current_step: string | null;
  email_attached: boolean;
  eligibility: EligibilityResult | null;
  paid: boolean;
  packet_available?: boolean;
};

/* ------------------------------------------------------------------ steps */

const STEPS = [
  { id: 'state', label: 'Your state' },
  { id: 'eligibility', label: 'Basics' },
  { id: 'parties', label: 'You & your spouse' },
  { id: 'marriage', label: 'Marriage' },
  { id: 'children', label: 'Children' },
  { id: 'property', label: 'Property & debts' },
  { id: 'support', label: 'Support' },
  { id: 'agreement', label: 'Agreement' },
  { id: 'service', label: 'Serving papers' },
  { id: 'fee-waiver', label: 'Court fees' },
  { id: 'review', label: 'Review' },
] as const;

function californiaDefaults(answers: DivorceAnswers): DivorceAnswers {
  return {
    ...answers,
    filing_mode: 'joint',
    service: { ...answers.service, spouse_will_sign_waiver: true },
  };
}

export const Route = createFileRoute('/divorce/interview')({
  validateSearch: (search: Record<string, unknown>) => ({
    claimToken: typeof search.claimToken === 'string' ? search.claimToken : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    planToken: typeof search.planToken === 'string' ? search.planToken : undefined,
  }),
  beforeLoad: async ({ search }) => {
    // Paid / in-progress resumes keep working after the product is taken dark.
    if (typeof search.claimToken === 'string' && search.claimToken) {
      return;
    }
    await requireDivorceProduct();
  },
  component: DivorceInterviewPage,
});

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function DivorceInterviewPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: Route.fullPath });
  const [step, setStep] = useState(0);
  const [claimToken, setClaimToken] = useState<string | null>(search.claimToken ?? null);
  const [jurisdictionCode, setJurisdictionCode] = useState<string>(search.state?.toUpperCase() ?? '');
  const [answers, setAnswers] = useState<DivorceAnswers>({});
  const [gate, setGate] = useState<EligibilityResult | null>(null);
  const [packetAvailable, setPacketAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [needsEmailChallenge, setNeedsEmailChallenge] = useState(false);
  const [challengeEmail, setChallengeEmail] = useState('');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadGeneration = useRef(0);
  const isCalifornia = jurisdictionCode === 'CA';
  const visibleSteps = useMemo(
    () => (isCalifornia ? STEPS.filter((s) => s.id !== 'service') : STEPS),
    [isCalifornia],
  );

  const persistSearch = useCallback(
    (token: string) => {
      void navigate({
        to: '/divorce/interview',
        search: { claimToken: token, state: undefined, planToken: undefined },
        replace: true,
      });
    },
    [navigate],
  );

  // Initial load: resume by claim token or create.
  useEffect(() => {
    const gen = ++loadGeneration.current;
    let cancelled = false;
    (async () => {
      try {
        if (search.claimToken) {
          const url = new URL(`${API_BASE_URL}/v1/divorce/draft/${search.claimToken}`);
          if (challengeEmail) url.searchParams.set('email', challengeEmail);
          const res = await fetch(url.toString());
          if (res.status === 403) {
            if (!cancelled && gen === loadGeneration.current) setNeedsEmailChallenge(true);
            return;
          }
          if (!res.ok) throw new Error('Draft not found');
          const data = (await res.json()) as DraftResponse;
          if (cancelled || gen !== loadGeneration.current) return;
          setNeedsEmailChallenge(false);
          setClaimToken(data.claim_token);
          setJurisdictionCode(data.jurisdiction_code ?? '');
          setAnswers(data.answers || {});
          setGate(data.eligibility);
          setPacketAvailable(Boolean(data.packet_available));
          const resumeSteps =
            data.jurisdiction_code === 'CA'
              ? STEPS.filter((s) => s.id !== 'service')
              : STEPS;
          const idx = resumeSteps.findIndex((s) => s.id === data.current_step);
          if (idx >= 0) setStep(idx);
          else if (data.current_step === 'service') {
            const feeIdx = resumeSteps.findIndex((s) => s.id === 'fee-waiver');
            if (feeIdx >= 0) setStep(feeIdx);
          }
          track('Divorce Interview Resumed', { state: data.jurisdiction_code ?? undefined });
          return;
        }

        const startingState = search.state?.toUpperCase() || undefined;
        const res = await fetch(`${API_BASE_URL}/v1/divorce/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jurisdiction_code: startingState,
            answers: startingState === 'CA' ? californiaDefaults({}) : {},
            current_step: 'state',
            attribution: {
              source: search.state ? 'state_page' : 'direct',
              state: startingState,
            },
          }),
        });
        if (!res.ok) throw new Error('Could not start interview');
        const data = (await res.json()) as DraftResponse;
        if (cancelled || gen !== loadGeneration.current) return;
        setClaimToken(data.claim_token);
        setAnswers(data.answers || {});
        setGate(data.eligibility);
        setPacketAvailable(Boolean(data.packet_available));
        persistSearch(data.claim_token);
        if (search.planToken) {
          await fetch(
            `${API_BASE_URL}/v1/divorce/draft/${data.claim_token}/import-parenting-plan`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ parenting_plan_claim_token: search.planToken }),
            },
          ).catch(() => undefined);
        }
        track('Divorce Interview Started', { state: search.state?.toUpperCase() });
      } catch {
        if (!cancelled && gen === loadGeneration.current) {
          setLoadError('We could not load your interview. Please refresh or start from the divorce hub.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.claimToken, search.state, search.planToken, persistSearch, challengeEmail]);

  useEffect(() => {
    track('Divorce Interview Step Viewed', {
      step: visibleSteps[step]?.id,
      state: jurisdictionCode || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const flushSave = useCallback(async () => {
    if (!claimToken) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/divorce/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          jurisdiction_code: jurisdictionCode || undefined,
          answers,
          current_step: visibleSteps[step]?.id ?? STEPS[step]?.id,
          challenge_email: challengeEmail || undefined,
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as DraftResponse;
        setGate(data.eligibility);
        setPacketAvailable(Boolean(data.packet_available));
      }
    } finally {
      setSaving(false);
    }
  }, [claimToken, answers, jurisdictionCode, step, challengeEmail, visibleSteps]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 600);
  }, [flushSave]);

  useEffect(() => {
    if (!claimToken) return;
    scheduleSave();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, jurisdictionCode, claimToken, scheduleSave]);

  const setSection = <K extends keyof DivorceAnswers>(key: K, value: DivorceAnswers[K]) => {
    setAnswers((a) => ({ ...a, [key]: value }));
  };

  useEffect(() => {
    if (!isCalifornia) return;
    setAnswers((a) => {
      if (a.filing_mode === 'joint' && a.service?.spouse_will_sign_waiver) return a;
      return californiaDefaults(a);
    });
  }, [isCalifornia]);

  const stepId = visibleSteps[step]?.id ?? STEPS[step]?.id;

  const canNext = (() => {
    if (stepId === 'state') return Boolean(jurisdictionCode);
    if (stepId === 'parties')
      return Boolean(answers.petitioner?.name?.trim() && answers.respondent?.name?.trim());
    return true;
  })();

  const isLastStep = step === visibleSteps.length - 1;

  const goNext = async () => {
    track('Divorce Interview Step Completed', {
      step: stepId,
      state: jurisdictionCode || undefined,
    });
    await flushSave();
    if (!isLastStep) setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  /* ------------------------- gate bounce-outs (blocked / wait) ------------ */

  const showGateScreen = gate && gate.status !== 'eligible' && step >= 1;

  if (loadError) {
    return (
      <div className='min-h-screen bg-white px-6 py-24 text-center'>
        <p className='text-gray-700' style={nunito}>{loadError}</p>
        <Link to='/divorce' className='mt-6 inline-block font-semibold text-primary'>
          Back to divorce hub
        </Link>
      </div>
    );
  }

  if (needsEmailChallenge) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white px-6'>
        <div className='w-full max-w-md rounded-2xl border border-gray-200 p-8 shadow-sm'>
          <h1 className='text-xl font-bold text-gray-900' style={nunito}>
            Confirm your email
          </h1>
          <p className='mt-2 text-sm text-gray-600' style={nunito}>
            This saved interview is protected. Enter the email address on the draft to continue.
          </p>
          <div className='mt-4'>
            <Input
              type='email'
              placeholder='you@example.com'
              value={challengeEmail}
              onChange={(e) => setChallengeEmail(e.target.value)}
            />
          </div>
          <p className='mt-3 text-xs text-gray-500' style={nunito}>
            The page reloads your draft automatically once the email matches.
          </p>
        </div>
      </div>
    );
  }

  if (!claimToken) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        <p className='text-gray-600' style={nunito}>Starting your interview…</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      <SEO
        title='Divorce interview | Harbor'
        description='Answer plain-language questions; Harbor prepares your official state divorce forms.'
        path='/divorce/interview'
        noindex
      />
      <header className='border-b border-gray-200 bg-white'>
        <div className='mx-auto flex max-w-6xl items-center justify-between px-4 py-4'>
          <Link
            to='/divorce'
            className='inline-flex items-center gap-2 text-sm font-medium text-primary'
            style={nunito}
          >
            <FiArrowLeft /> Divorce hub
          </Link>
          <span className='text-xs text-gray-500'>{saving ? 'Saving…' : 'Saved'}</span>
        </div>
      </header>

      <div className='mx-auto max-w-6xl px-4 pt-6'>
        <p className='rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-900' style={nunito}>
          {DOC_PREP_DISCLAIMER}
        </p>
      </div>

      {showGateScreen ? (
        <GateScreen gate={gate} onBack={() => setStep(1)} />
      ) : (
        <WizardShell
          steps={[...visibleSteps]}
          activeIndex={step}
          onBack={goBack}
          onNext={goNext}
          canGoBack={step > 0}
          canGoNext={canNext}
          nextLabel={isLastStep ? 'Finish' : 'Continue'}
        >
          {stepId === 'state' && (
            <StateStep
              jurisdictionCode={jurisdictionCode}
              setJurisdictionCode={setJurisdictionCode}
              answers={answers}
              setSection={setSection}
            />
          )}
          {stepId === 'eligibility' && (
            <EligibilityStep answers={answers} setSection={setSection} />
          )}
          {stepId === 'parties' && (
            <PartiesStep
              answers={answers}
              setSection={setSection}
              isCalifornia={isCalifornia}
            />
          )}
          {stepId === 'marriage' && <MarriageStep answers={answers} setSection={setSection} />}
          {stepId === 'children' && <ChildrenStep answers={answers} setSection={setSection} />}
          {stepId === 'property' && <PropertyStep answers={answers} setSection={setSection} />}
          {stepId === 'support' && <SupportStep answers={answers} setSection={setSection} />}
          {stepId === 'agreement' && (
            <AgreementStep
              answers={answers}
              setSection={setSection}
              claimToken={claimToken}
              challengeEmail={challengeEmail}
              onImported={(a) => setAnswers(a)}
            />
          )}
          {stepId === 'service' && <ServiceStep answers={answers} setSection={setSection} />}
          {stepId === 'fee-waiver' && (
            <FeeWaiverStep
              answers={answers}
              setSection={setSection}
              isCalifornia={isCalifornia}
            />
          )}
          {stepId === 'review' && (
            <ReviewStep
              answers={answers}
              jurisdictionCode={jurisdictionCode}
              gate={gate}
              packetAvailable={packetAvailable}
              claimToken={claimToken}
              challengeEmail={challengeEmail}
              onBeforeCheckout={flushSave}
            />
          )}
        </WizardShell>
      )}

      <Footer />
    </div>
  );
}

/* --------------------------------------------------------------- gate UI */

function GateScreen({ gate, onBack }: { gate: EligibilityResult; onBack: () => void }) {
  const isWait = gate.status === 'wait';
  return (
    <div className='mx-auto max-w-3xl px-4 py-12'>
      <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'>
        <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
          {isWait ? 'Not quite yet — but your answers are saved' : 'This path isn’t the right fit'}
        </h2>
        <div className='mt-4 space-y-4'>
          {gate.reasons.map((r) => (
            <div key={r.code} className='rounded-xl bg-gray-50 p-4'>
              <p className='text-gray-800' style={nunito}>
                {r.message}
              </p>
              {r.resource_url ? (
                <a
                  href={r.resource_url}
                  className='mt-2 inline-block text-sm font-semibold text-primary'
                  target={r.resource_url.startsWith('http') ? '_blank' : undefined}
                  rel='noreferrer'
                >
                  {r.code === 'dv_safety' ? 'National Domestic Violence Hotline' : 'Find a professional'}
                </a>
              ) : null}
            </div>
          ))}
        </div>
        <div className='mt-8 flex flex-wrap gap-3'>
          <Link
            to='/divorce'
            className='rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white'
          >
            See your state’s free checklist
          </Link>
          <button
            type='button'
            onClick={onBack}
            className='rounded-2xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800'
          >
            Review my answers
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ step pieces */

function YesNo({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div className='rounded-xl border border-gray-200 bg-white p-4'>
      <p className='font-medium text-gray-900' style={nunito}>
        {label}
      </p>
      {hint ? (
        <p className='mt-1 text-sm text-gray-500' style={nunito}>
          {hint}
        </p>
      ) : null}
      <div className='mt-3 flex gap-2'>
        {[
          { v: true, label: 'Yes' },
          { v: false, label: 'No' },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type='button'
            onClick={() => onChange(opt.v)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
              value === opt.v
                ? 'bg-primary text-white'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type SectionSetter = <K extends keyof DivorceAnswers>(key: K, value: DivorceAnswers[K]) => void;

function StateStep({
  jurisdictionCode,
  setJurisdictionCode,
  answers,
  setSection,
}: {
  jurisdictionCode: string;
  setJurisdictionCode: (code: string) => void;
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const residency = answers.residency ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Where will you file?
      </h2>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>State</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={jurisdictionCode}
          onChange={(e) => setJurisdictionCode(e.target.value)}
        >
          <option value=''>Select your state…</option>
          {DIVORCE_JURISDICTIONS.map((j) => (
            <option key={j.code} value={j.code}>
              {j.name}
            </option>
          ))}
        </select>
      </div>
      <div className='grid gap-4 sm:grid-cols-2'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Months you’ve lived in this state
          </label>
          <Input
            type='number'
            min={0}
            value={residency.months_in_state ?? ''}
            onChange={(e) =>
              setSection('residency', {
                ...residency,
                months_in_state: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Months you’ve lived in this county
          </label>
          <Input
            type='number'
            min={0}
            value={residency.months_in_county ?? ''}
            onChange={(e) =>
              setSection('residency', {
                ...residency,
                months_in_county: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
        <div className='sm:col-span-2'>
          <label className='mb-1 block text-sm font-medium text-gray-700'>County</label>
          <Input
            value={residency.county ?? ''}
            onChange={(e) => setSection('residency', { ...residency, county: e.target.value })}
          />
        </div>
      </div>
      <YesNo
        label='Are you in the state because of a military assignment?'
        hint='Being stationed somewhere isn’t always the same as being a resident.'
        value={residency.military}
        onChange={(v) => setSection('residency', { ...residency, military: v })}
      />
    </div>
  );
}

function EligibilityStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const e = answers.eligibility ?? {};
  const service = answers.service ?? {};
  const set = (key: string, v: boolean) => setSection('eligibility', { ...e, [key]: v });
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        A few questions first
      </h2>
      <p className='text-gray-600' style={nunito}>
        These make sure a do-it-yourself divorce is actually the right tool for your situation.
        Some answers point you to better help instead.
      </p>
      <YesNo
        label='Do you want a divorce (not an annulment)?'
        value={e.wants_annulment === undefined ? undefined : !e.wants_annulment}
        onChange={(v) => set('wants_annulment', !v)}
      />
      <YesNo
        label={DV_SCREEN_QUESTION}
        hint={DV_SCREEN_HINT}
        value={e.dv_flag === undefined ? undefined : !e.dv_flag}
        onChange={(v) => set('dv_flag', !v)}
      />
      <YesNo
        label='Do you and your spouse agree on all major terms?'
        hint='Property, debts, support, and (if you have kids) the parenting schedule.'
        value={e.contested === undefined ? undefined : !e.contested}
        onChange={(v) => set('contested', !v)}
      />
      <YesNo
        label='Is your marriage a covenant marriage?'
        hint='A special marriage type in Arizona, Arkansas, and Louisiana. Most marriages are not.'
        value={e.covenant_marriage}
        onChange={(v) => set('covenant_marriage', v)}
      />
      <YesNo
        label='Is either of you in an active bankruptcy case?'
        value={e.active_bankruptcy}
        onChange={(v) => set('active_bankruptcy', v)}
      />
      <YesNo
        label='Any open family-law cases between you (custody, protective orders, an earlier divorce)?'
        value={e.pending_cases}
        onChange={(v) => set('pending_cases', v)}
      />
      <YesNo
        label='Was either of you married before — and if so, is that divorce final?'
        hint='Answer “No” only if a previous divorce was never finalized.'
        value={e.prior_marriage_not_final === undefined ? undefined : !e.prior_marriage_not_final}
        onChange={(v) => set('prior_marriage_not_final', !v)}
      />
      <YesNo
        label='Is your spouse on active military duty?'
        value={e.spouse_military}
        onChange={(v) => set('spouse_military', v)}
      />
      {e.spouse_military ? (
        <YesNo
          label='Will your spouse sign a waiver or acceptance of service?'
          value={service.spouse_will_sign_waiver}
          onChange={(v) => setSection('service', { ...service, spouse_will_sign_waiver: v })}
        />
      ) : null}
      <YesNo
        label='Is anyone currently pregnant?'
        value={e.pregnant}
        onChange={(v) => set('pregnant', v)}
      />
      <YesNo
        label='Is child support already handled by a state agency, or does either parent receive public benefits (TANF, Medicaid)?'
        value={e.iv_d_case}
        onChange={(v) => set('iv_d_case', v)}
      />
    </div>
  );
}

function PartyFields({
  title,
  party,
  onChange,
}: {
  title: string;
  party: Party;
  onChange: (p: Party) => void;
}) {
  return (
    <div className='rounded-xl border border-gray-200 bg-white p-4'>
      <h3 className='mb-3 font-semibold text-gray-900' style={nunito}>
        {title}
      </h3>
      <div className='grid gap-3 sm:grid-cols-2'>
        <Input
          label='Full legal name'
          value={party.name ?? ''}
          onChange={(e) => onChange({ ...party, name: e.target.value })}
        />
        <Input
          label='Phone'
          value={party.phone ?? ''}
          onChange={(e) => onChange({ ...party, phone: e.target.value })}
        />
        <Input
          label='Street address'
          value={party.address ?? ''}
          onChange={(e) => onChange({ ...party, address: e.target.value })}
        />
        <Input
          label='City'
          value={party.city ?? ''}
          onChange={(e) => onChange({ ...party, city: e.target.value })}
        />
        <Input
          label='State'
          value={party.state ?? ''}
          onChange={(e) => onChange({ ...party, state: e.target.value })}
        />
        <Input
          label='ZIP'
          value={party.zip ?? ''}
          onChange={(e) => onChange({ ...party, zip: e.target.value })}
        />
        <Input
          label='Email'
          type='email'
          value={party.email ?? ''}
          onChange={(e) => onChange({ ...party, email: e.target.value })}
        />
      </div>
    </div>
  );
}

function PartiesStep({
  answers,
  setSection,
  isCalifornia,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  isCalifornia: boolean;
}) {
  const joint = isCalifornia || answers.filing_mode === 'joint';
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Who is filing?
      </h2>
      {isCalifornia ? (
        <p className='text-gray-600' style={nunito}>
          California’s joint petition lists you as Petitioner 1 and Petitioner 2 — there is no
          legal advantage to either slot, but the same person must stay 1 or 2 on every form. Both
          of you sign FL-700 and FL-710; no one has to be served.
        </p>
      ) : (
        <p className='text-gray-600' style={nunito}>
          Court forms use “Petitioner” (the person filing) and “Respondent” (the other spouse) —
          never husband or wife. In some states you can file together as co-petitioners.
        </p>
      )}
      {isCalifornia ? null : (
        <div className='flex gap-2'>
          {[
            { v: 'sole' as const, label: 'I’m filing' },
            { v: 'joint' as const, label: 'We’re filing together' },
          ].map((opt) => (
            <button
              key={opt.v}
              type='button'
              onClick={() => setSection('filing_mode', opt.v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                (answers.filing_mode ?? 'sole') === opt.v
                  ? 'bg-primary text-white'
                  : 'border border-gray-300 bg-white text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      <PartyFields
        title={isCalifornia ? 'You (Petitioner 1)' : joint ? 'You (co-petitioner 1)' : 'You (petitioner)'}
        party={answers.petitioner ?? {}}
        onChange={(p) => setSection('petitioner', p)}
      />
      <PartyFields
        title={
          isCalifornia
            ? 'Your spouse (Petitioner 2)'
            : joint
              ? 'Your spouse (co-petitioner 2)'
              : 'Your spouse (respondent)'
        }
        party={answers.respondent ?? {}}
        onChange={(p) => setSection('respondent', p)}
      />
      <YesNo
        label='Does either of you want a former name restored as part of the divorce?'
        value={answers.name_change_requested}
        onChange={(v) => setSection('name_change_requested', v)}
      />
    </div>
  );
}

function MarriageStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const m = answers.marriage ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Your marriage
      </h2>
      <div className='grid gap-4 sm:grid-cols-2'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>Date of marriage</label>
          <Input
            type='date'
            value={m.date ?? ''}
            onChange={(e) => setSection('marriage', { ...m, date: e.target.value })}
          />
        </div>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700'>
            Date of separation (if separated)
          </label>
          <Input
            type='date'
            value={m.separation_date ?? ''}
            onChange={(e) => setSection('marriage', { ...m, separation_date: e.target.value })}
          />
        </div>
        <Input
          label='City married in'
          value={m.city ?? ''}
          onChange={(e) => setSection('marriage', { ...m, city: e.target.value })}
        />
        <Input
          label='State/country married in'
          value={m.state ?? ''}
          onChange={(e) => setSection('marriage', { ...m, state: e.target.value })}
        />
      </div>
      <YesNo
        label='Is this a common-law (informal) marriage?'
        hint='Recognized in a few states, like Texas and Colorado.'
        value={m.common_law}
        onChange={(v) => setSection('marriage', { ...m, common_law: v })}
      />
    </div>
  );
}

function ChildrenStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const children = answers.children ?? [];
  const update = (idx: number, patch: Partial<Child>) => {
    const next = [...children];
    next[idx] = { ...next[idx], ...patch };
    setSection('children', next);
  };
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Children of the marriage
      </h2>
      <p className='text-gray-600' style={nunito}>
        Skip this step if you don’t have children together. Include all children under 18 (adult
        children can still matter for support in some states — include them and the court will
        sort it out).
      </p>
      {children.map((c, idx) => (
        <div key={idx} className='rounded-xl border border-gray-200 bg-white p-4'>
          <div className='grid gap-3 sm:grid-cols-3'>
            <Input
              label='Name'
              value={c.name ?? ''}
              onChange={(e) => update(idx, { name: e.target.value })}
            />
            <div>
              <label className='mb-1 block text-sm text-gray-600'>Date of birth</label>
              <Input
                type='date'
                value={c.dob ?? ''}
                onChange={(e) => update(idx, { dob: e.target.value })}
              />
            </div>
            <div>
              <label className='mb-1 block text-sm text-gray-600'>Lives mostly with</label>
              <select
                className='w-full rounded-xl border border-gray-200 bg-white px-4 py-3'
                value={c.lives_with ?? ''}
                onChange={(e) =>
                  update(idx, { lives_with: (e.target.value || undefined) as Child['lives_with'] })
                }
              >
                <option value=''>Select…</option>
                <option value='petitioner'>Me</option>
                <option value='respondent'>My spouse</option>
                <option value='both'>Both of us equally</option>
                <option value='other'>Someone else</option>
              </select>
            </div>
          </div>
          <button
            type='button'
            className='mt-3 text-sm font-semibold text-red-600'
            onClick={() => setSection('children', children.filter((_, i) => i !== idx))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type='button'
        className='text-sm font-semibold text-primary'
        onClick={() => setSection('children', [...children, {}])}
      >
        + Add a child
      </button>
    </div>
  );
}

function PropertyStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const p = answers.property ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Property & debts
      </h2>
      <YesNo
        label='Do you own real estate together?'
        value={p.has_real_estate}
        onChange={(v) => setSection('property', { ...p, has_real_estate: v })}
      />
      <YesNo
        label='Does either of you have retirement accounts to divide?'
        hint='Splitting retirement usually needs a separate court order (QDRO). We’ll flag this for attorney review.'
        value={p.has_retirement}
        onChange={(v) => setSection('property', { ...p, has_retirement: v })}
      />
      <YesNo
        label='Does either of you own a business?'
        value={p.has_business}
        onChange={(v) => setSection('property', { ...p, has_business: v })}
      />
    </div>
  );
}

function SupportStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const s = answers.support ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Support
      </h2>
      <YesNo
        label='Will one of you pay child support?'
        value={s.child_support_requested}
        onChange={(v) => setSection('support', { ...s, child_support_requested: v })}
      />
      <YesNo
        label='Will one of you pay spousal support (alimony)?'
        value={s.spousal_support_requested}
        onChange={(v) => setSection('support', { ...s, spousal_support_requested: v })}
      />
      <p className='text-sm text-gray-500' style={nunito}>
        Need a number? Our free{' '}
        <Link to='/child-support-calculator' className='font-semibold text-primary'>
          child support calculator
        </Link>{' '}
        uses your state’s guidelines.
      </p>
    </div>
  );
}

function AgreementStep({
  answers,
  setSection,
  claimToken,
  challengeEmail,
  onImported,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  claimToken: string;
  challengeEmail: string;
  onImported: (a: DivorceAnswers) => void;
}) {
  const a = answers.agreement ?? {};
  const [planToken, setPlanToken] = useState('');
  const [importState, setImportState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const importPlan = async () => {
    if (!planToken.trim()) return;
    setImportState('loading');
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/divorce/draft/${claimToken}/import-parenting-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parenting_plan_claim_token: planToken.trim(),
            challenge_email: challengeEmail || undefined,
          }),
        },
      );
      if (!res.ok) throw new Error('import failed');
      const data = (await res.json()) as DraftResponse;
      onImported(data.answers);
      setImportState('done');
      track('Divorce Interview Parenting Plan Imported');
    } catch {
      setImportState('error');
    }
  };

  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Your agreement
      </h2>
      <div className='rounded-xl border border-primary/30 bg-primary/5 p-4'>
        <p className='font-medium text-gray-900' style={nunito}>
          Already built a parenting plan with Harbor?
        </p>
        <p className='mt-1 text-sm text-gray-600' style={nunito}>
          Paste the link or code from your parenting plan email and we’ll import your schedule.
        </p>
        <div className='mt-3 flex flex-wrap gap-2'>
          <div className='min-w-[240px] flex-1'>
            <Input
              placeholder='Parenting plan code'
              value={planToken}
              onChange={(e) => setPlanToken(e.target.value)}
            />
          </div>
          <button
            type='button'
            onClick={importPlan}
            disabled={importState === 'loading'}
            className='rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white disabled:opacity-50'
          >
            {importState === 'loading' ? 'Importing…' : 'Import'}
          </button>
        </div>
        {importState === 'done' ? (
          <p className='mt-2 text-sm font-medium text-green-700'>Imported.</p>
        ) : null}
        {importState === 'error' ? (
          <p className='mt-2 text-sm font-medium text-red-600'>
            We couldn’t find that plan. Check the code and try again.
          </p>
        ) : null}
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>
          Custody schedule (if you have children)
        </label>
        <Input
          placeholder='e.g. 2-2-3, week-on-week-off'
          value={a.schedule_slug ?? ''}
          onChange={(e) => setSection('agreement', { ...a, schedule_slug: e.target.value })}
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Holidays</label>
        <Input
          placeholder='e.g. alternate major holidays each year'
          value={a.holidays ?? ''}
          onChange={(e) => setSection('agreement', { ...a, holidays: e.target.value })}
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Major decisions</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={a.decision_making ?? ''}
          onChange={(e) =>
            setSection('agreement', {
              ...a,
              decision_making: (e.target.value || undefined) as
                | 'joint'
                | 'petitioner'
                | 'respondent'
                | undefined,
            })
          }
        >
          <option value=''>Select…</option>
          <option value='joint'>We decide together</option>
          <option value='petitioner'>I decide</option>
          <option value='respondent'>My spouse decides</option>
        </select>
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Expense split</label>
        <Input
          placeholder='e.g. 50/50 after receipts'
          value={a.expense_split ?? ''}
          onChange={(e) => setSection('agreement', { ...a, expense_split: e.target.value })}
        />
      </div>
    </div>
  );
}

function ServiceStep({
  answers,
  setSection,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
}) {
  const s = answers.service ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Serving the papers
      </h2>
      <p className='text-gray-600' style={nunito}>
        After filing, your spouse has to be formally notified. In amicable cases they usually just
        sign a waiver — no process server at the door.
      </p>
      <YesNo
        label='Will your spouse sign a waiver or acceptance of service?'
        value={s.spouse_will_sign_waiver}
        onChange={(v) => setSection('service', { ...s, spouse_will_sign_waiver: v })}
      />
      <YesNo
        label='Do you know your spouse’s current address?'
        value={s.spouse_address_known}
        onChange={(v) => setSection('service', { ...s, spouse_address_known: v })}
      />
      <YesNo
        label='Do you still live at the same address?'
        hint='Service rules differ for separated-but-cohabiting couples.'
        value={s.same_household}
        onChange={(v) => setSection('service', { ...s, same_household: v })}
      />
    </div>
  );
}

function FeeWaiverStep({
  answers,
  setSection,
  isCalifornia,
}: {
  answers: DivorceAnswers;
  setSection: SectionSetter;
  isCalifornia: boolean;
}) {
  const f = answers.fee_waiver ?? {};
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Court filing fees
      </h2>
      <p className='text-gray-600' style={nunito}>
        {isCalifornia
          ? 'A California joint petition is two first-paper fees — about $870 total. If you cannot afford it, each of you may need your own fee waiver (FW-001); one waiver does not cover both of you.'
          : 'Courts charge a filing fee, but every state lets low-income filers ask for a waiver.'}
      </p>
      <YesNo
        label='Do you want to apply for a fee waiver?'
        value={f.requested}
        onChange={(v) => setSection('fee_waiver', { ...f, requested: v })}
      />
      {f.requested ? (
        <div className='grid gap-4 sm:grid-cols-2'>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>Household size</label>
            <Input
              type='number'
              min={1}
              value={f.household_size ?? ''}
              onChange={(e) =>
                setSection('fee_waiver', {
                  ...f,
                  household_size: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>
              Monthly household income ($)
            </label>
            <Input
              type='number'
              min={0}
              value={f.monthly_income_cents != null ? f.monthly_income_cents / 100 : ''}
              onChange={(e) =>
                setSection('fee_waiver', {
                  ...f,
                  monthly_income_cents:
                    e.target.value === '' ? undefined : Math.round(Number(e.target.value) * 100),
                })
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReviewStep({
  answers,
  jurisdictionCode,
  gate,
  packetAvailable,
  claimToken,
  challengeEmail,
  onBeforeCheckout,
}: {
  answers: DivorceAnswers;
  jurisdictionCode: string;
  gate: EligibilityResult | null;
  packetAvailable: boolean;
  claimToken: string;
  challengeEmail: string;
  onBeforeCheckout: () => Promise<void>;
}) {
  const jurisdiction = DIVORCE_JURISDICTIONS.find((j) => j.code === jurisdictionCode);
  const isCalifornia = jurisdictionCode === 'CA';
  const [checkoutState, setCheckoutState] = useState<'idle' | 'loading' | 'error'>('idle');

  const startCheckout = async () => {
    setCheckoutState('loading');
    try {
      track('Divorce Packet Checkout Started', { state: jurisdictionCode || undefined });
      await onBeforeCheckout();
      const res = await fetch(`${API_BASE_URL}/v1/subscription/create-divorce-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          challenge_email: challengeEmail || undefined,
        }),
      });
      if (!res.ok) throw new Error('checkout failed');
      const data = (await res.json()) as { checkout_url: string };
      window.location.href = data.checkout_url;
    } catch {
      setCheckoutState('error');
    }
  };
  const rows: [string, string | undefined][] = [
    ['State', jurisdiction?.name],
    [
      'Filing',
      isCalifornia || answers.filing_mode === 'joint'
        ? 'Joint petition (both sign)'
        : 'One spouse filing',
    ],
    [isCalifornia ? 'Petitioner 1' : 'You', answers.petitioner?.name],
    [isCalifornia ? 'Petitioner 2' : 'Your spouse', answers.respondent?.name],
    ['Married', answers.marriage?.date],
    ['Children', answers.children?.length ? String(answers.children.length) : 'None'],
    ['Custody schedule', answers.agreement?.schedule_slug],
    ['Fee waiver', answers.fee_waiver?.requested ? 'Requested' : 'Not requested'],
  ];
  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
        Review
      </h2>
      <div className='overflow-hidden rounded-xl border border-gray-200 bg-white'>
        {rows.map(([label, value]) => (
          <div key={label} className='flex justify-between border-b border-gray-100 px-4 py-3 last:border-0'>
            <span className='text-sm text-gray-500' style={nunito}>
              {label}
            </span>
            <span className='text-sm font-medium text-gray-900' style={nunito}>
              {value || '—'}
            </span>
          </div>
        ))}
      </div>
      {gate?.warnings?.length ? (
        <div className='space-y-3'>
          {gate.warnings.map((w) => (
            <div key={w.code} className='rounded-xl bg-amber-50 p-4 text-sm text-amber-900' style={nunito}>
              {w.message}{' '}
              {w.resource_url ? (
                <a href={w.resource_url} className='font-semibold underline'>
                  Find a professional
                </a>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <div className='rounded-2xl border border-primary/30 bg-primary/5 p-6'>
        {packetAvailable ? (
          <>
            <h3 className='text-lg font-bold text-gray-900' style={nunito}>
              Get your completed {jurisdiction?.name ?? 'state'} forms — $149
            </h3>
            <p className='mt-2 text-gray-700' style={nunito}>
              {isCalifornia
                ? 'Both of you sign FL-700 and FL-710 — no service. One payment covers the filled official forms, free regeneration if California updates them, and 12 months of Harbor Pro. Full refund any time before your packet is generated.'
                : 'One payment covers your filled official court forms, free regeneration whenever your state updates its forms, and 12 months of Harbor Pro for life after the divorce. Full refund any time before your packet is generated.'}
            </p>
            <div className='mt-4 flex flex-wrap items-center gap-3'>
              <button
                type='button'
                onClick={startCheckout}
                disabled={checkoutState === 'loading'}
                className='rounded-2xl bg-primary px-6 py-3 text-sm font-semibold text-white disabled:opacity-50'
              >
                {checkoutState === 'loading' ? 'Opening checkout…' : 'Get my packet — $149'}
              </button>
              <Link to='/divorce' className='text-sm font-semibold text-primary'>
                Or keep using the free checklist
              </Link>
            </div>
            {checkoutState === 'error' ? (
              <p className='mt-3 text-sm font-medium text-red-600' style={nunito}>
                Checkout isn’t available right now — your answers are saved, try again shortly.
              </p>
            ) : null}
          </>
        ) : (
          <>
            <h3 className='text-lg font-bold text-gray-900' style={nunito}>
              Packet prep for {jurisdiction?.name ?? 'this state'} is not open yet
            </h3>
            <p className='mt-2 text-gray-700' style={nunito}>
              Your answers are saved. Harbor’s free checklist is ready now; we’ll prepare official
              forms here once this state’s packet is reviewed.
            </p>
            <Link to='/divorce' className='mt-4 inline-block text-sm font-semibold text-primary'>
              Use the free checklist
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
